import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from '../../services/AdminService/admin';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { findCountry } from '../../shared/countries';

@Component({
  selector: 'app-upsert-field-rule-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    ErrorBanner,
  ],
  templateUrl: './upsert-field-rule-dialog.html',
  styleUrl: './upsert-field-rule-dialog.scss',
})
export class UpsertFieldRuleDialog implements OnInit {
  countries = signal<string[]>([]);
  terms = signal<string[]>([]);

  countryCode = '';
  term = '';
  eventType = '';
  localName = '';
  required = false;

  isSubmitting = signal(false);
  errorMessage = signal('');

  constructor(private dialogRef: MatDialogRef<UpsertFieldRuleDialog>, private admin: AdminService) {}

  ngOnInit(): void {
    forkJoin({
      terms: this.admin.listGeneralTerms(),
      rules: this.admin.listTermRules(),
    }).subscribe({
      next: ({ terms, rules }) => {
        const termList = terms.data.map((t) => t.term).sort();
        this.terms.set(termList);
        if (!this.term && termList.length > 0) {
          this.term = termList[0];
        }

        const countryList = Array.from(new Set(rules.data.map((r) => r.countryCode))).sort();
        this.countries.set(countryList);
        if (!this.countryCode && countryList.length > 0) {
          this.countryCode = countryList[0];
        }
      },
      error: (err: HttpErrorResponse) => {
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load the term catalog.'));
      },
    });
  }

  countryLabel(code: string): string {
    const country = findCountry(code);
    return country ? `${country.name} (${code})` : code;
  }

  get canSubmit(): boolean {
    return !!this.countryCode.trim() && !!this.term;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    this.admin
      .upsertTermRule(this.countryCode.toUpperCase(), this.term, this.eventType || undefined, {
        localName: this.localName || undefined,
        required: this.required,
        onChain: this.required,
      })
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          this.isSubmitting.set(false);
          this.errorMessage.set(extractErrorMessage(err, 'Failed to save term rule.'));
        },
      });
  }
}
